import angular from 'angular';
import Rx from 'rx';
import 'ng-csv';
import print from 'print-js';
import Highcharts from 'highcharts';
import ChartsTheme from './charts-theme';
import Chart from './chart';
import {flattenBreakdownData,addMissingZeroDays} from './chartResponse';
import {translateKey,buildOverrides} from './chartSerie';
import ConfigsModule from '../../configs/configs';
import ServicesModule from '../../services/services';
import ResourcesModule from '../../resources/resources';
import template from './analyticsPanel.jade';

export class AnalyticsPanelController {
  /* @ngInject */
  constructor($q, $filter, $stateParams, currentGame, ggCSV, featureFlagsHelper) {
    this.$q = $q;
    this.translate = $filter('translate');
    this.$stateParams = $stateParams;
    this.csv = this.csv || new ggCSV();
    this.disposables = [];

    this.$onInit = function() {
      this.scopeName = this.scopeName || this.parent.scopeName;
      this.analyticsExplore = this.analyticsExplore || this.parent.analyticsExplore;
      this.campaignsExplore = this.campaignsExplore || this.parent.campaignsExplore;
      this.exploreParams = _.assign(
        {},
        this.$stateParams,
        {axisName: this.campaignsExplore || this.analyticsExplore || this.metric}
      );
      this.hideHeader = this.hideHeader || this.parent.hideHeader;
      this.queryResource = this.resource || this.parent.resource;
      this.id = this.id || this.parent.id;
      this.isPanelVisible = new Rx.BehaviorSubject(false);
      this.isContextReady = this.contextReadySubject || new Rx.BehaviorSubject(true);
      this.setupQuerySubject(this.parent.query, this.isPanelVisible, this.isContextReady);
    };

    this.disposeObservable = (d) => { d.dispose() };
    this.$onDestroy = () => {
      _.forEach(this.disposables, (d) => { this.disposeObservable(d) });
    };

    this.disposables.push(currentGame.subscribe(game => {
      if (game) {
        this.game = game;
        this.csv.basename = game.name.replace(/\s/, '_');
        this.csv.overrideCsvFilename = this.overrideCsvFilename;
      }
    }));
    this.query = this.query || {};
    this.createNewChart();
    if (this.seriesOptions) {
      this.selectedSerie = _.find(this.seriesOptions, {metric: this.seriesOptionsSelected}) || this.seriesOptions[0];
      this.setSeriesOnSelectedSerie(this.selectedSerie);
    }
    this.groupByDateOptions = [
      {value: false, name: this.translate('grouped')},
      {value: true, name: this.translate('daily')}
    ];
    if (this.groupByDateChange) {
      this.disposables.push(this.groupByDateChange.subscribe((groupByDate) => {
        this.groupByDate = groupByDate;
      }));
    }
  }

  print() {
    print({
      printable: this.id,
      type: 'html',
      targetStyles: ['*'],
      maxWidth: 'auto',
      style: '@page { size: Letter landscape; }'
    })
  }

  translateKey(metric, field, seriesName) {
    return translateKey(this.scopeName, seriesName || this.seriesName, metric, field);
  }

  triggerIsPanelVisible(inview) {
    this.isPanelVisible.onNext(inview);
  }

  setupQuerySubject(query$, isVisible$, isContextReady$) {
    this.queryIfIsVisibleAndContextReady$ = Rx.Observable.combineLatest(
      query$,
      isVisible$,
      isContextReady$,
      (query, isVisible, isContextReady) => {
        return { isVisible, query, isContextReady };
      }
    )
      .filter(({isVisible}) => {
        return isVisible;
      })
      .filter(({isContextReady}) => {
        return isContextReady;
      })
      .map(({query}) => {
        return query;
      })
      .distinctUntilChanged(JSON.stringify);
    this.disposables.push(this.queryIfIsVisibleAndContextReady$
      .subscribe((query) => {
        this.processQuery(query);
      }));
  }

  createNewChart() {

    this.chart = new Chart(this.config);
    this.chart.seriesLimit = this.seriesLimit;
    this.chart.endpointError = false;
    this.chart.loading = false;
  }

  processQuery(query) {
    this.createNewChart();
    this.query = this.decorateQuery ? this.decorateQuery() : query;
    this.chart.loading = true;

    this.getData(_.omit(query, 'refresherCount'))
      .then(res => {
        return flattenBreakdownData(res);
      })
      .then(res => {
        if (this.areSeriesNotDateBased) {
          return res;
        } else {
          return addMissingZeroDays(res);
        }
      })
      .catch(err => {
        this.chart.endpointError = true;
        return this.$q.reject();
      })
      .then(res => {
        this.chart.loading = false;
        this.dataRetrievedCallback(res);
        res = this.transformResponse(res);
        res = this.transformSerieResponse(res);
        if (!res.length)
          res = [{data: {rows: []}, query: this.query}];
        this.updateSeries(res);
        !this.skipCsv && this.updateCsv(res);
        this.updateTable(res);
      });
  }

  transformSerieResponse(res) {
    return res.map((r, index) => {
      let serie = this.getSerieByIndex(index);
      r.query = _.cloneDeep(this.query);
      if (this.query.metric)
        r.query.metric = serie.metric;
      return serie.transformSerieResponse ? serie.transformSerieResponse(r, serie) : r;
    });
  }

  transformResponse(res) {
    let transformResponse = _.get(this.series,'[0].transformResponse');
    return transformResponse ? transformResponse(res, this.seriesName) : res;
  }

  // We use this just to notify eny external component who is using this component that data has been retrieved.
  // Given the current flow, the data retrieval (API call) is done here and if the external component needs the same data
  // e.g. to build a table should be able to know when the data is ready without getting into the flow of the chart.
  //
  // the optimal flow would have the controller responsible to fetch data and notify any other sub component like a chart or a table
  // that new data is available
  dataRetrievedCallback(res) {
    let dataRetrievedCallback = _.get(this.series,'[0].dataRetrievedCallback');
    dataRetrievedCallback ? dataRetrievedCallback(res, this.seriesName) : null;
  }

  getData(query) {
    let getData = ((serie, params) => {
      return serie.getData ? serie.getData(params) : this.queryResource.one().get(params);
    });

    let seriesPromises = this.series.map(serie => {
      return getData(serie, this.queryForSerie(query, serie));
    });

    let seriesComparisonsPromises = [];
    if (this.comparison) {
      seriesComparisonsPromises = this.series.map(serie => {
        let queryWithComparison = _.merge(this.queryForSerie(query, serie), query.comparison);
        return getData(serie, queryWithComparison);
      });
    }

    if (this.processOnlyFirstSeries) {
      seriesPromises = seriesPromises.length ? [seriesPromises[1]] : [];
    }

    // ordered series promises i.e. [serie_1, serie_1_comparison, serie_2, serie_2_comparison]
    return this.$q.all(_.chain(seriesPromises).zip(seriesComparisonsPromises).flatten().compact().value());
  }

  queryForSerie(query, serie) {
    return _.merge(
      {},
      _.omit(serie.queryParams, 'multipleDimensions'),
      _.omit(query, 'comparison'),
      {metric: serie.metric}
    );
  }

  updateSeries(res) {
    this.setOverrides(res);
    let series = _.map(res, 'data');
    this.chart.setComparison(this.comparison);
    if(this.tooltipShare != 'false') this.chart.setTooltipShare(this.comparison || this.breakdown || this.tooltipShare);
    this.chart.setYAxis(series, this.forceSingleYAxis, this.isYAxisLogarithmic, this.customYAxis);
    this.chart.setEventHandlers(series);
    this.chart.setTooltipType(this.tooltipType);
    if(this.isXAxisCategory) {
      this.chart.setXAxisCategories(series);
    } else {
      this.chart.setXAxisDates();
    }
    this.chart.series = series;
    if(this.drilldownConfig){
      this.chart.setDrilldown(this.drilldownConfig);
    }
  }

  setOverrides(res) {
    return res.map((r, index) => {
      let serie = this.getSerieByIndex(index);
      r.data.overrides = buildOverrides(r.data, serie, this.translate, this.scopeName, this.seriesName);
      if (serie.serieCopy) {
        let newSerie = _.merge({}, serie, serie.serieCopy);
        r.data.overrides.serieCopy = buildOverrides(r.data, newSerie, this.translate, this.scopeName, this.seriesName);
      }
      return r;
    });
  }

  applySerieThumbnailTooltips(series) {
    series
      .filter(serie => serie.overrides.applySerieThumbnailTooltips)
      .map(serie => serie.overrides.applySerieThumbnailTooltips(serie, this.query));

    this.chart.series = series;
  }

  postProcessSeries(series) {
    series = series.filter((serie) => {
      return serie.overrides.postProcessSeries;
    });
    if(!_.isEmpty(series)) {
      series = series[0].overrides.postProcessSeries(series, this.query);
    }
    return series;
  }

  getSerieByIndex(index) {
    if (this.comparison) {
      let seriesComparisons = this.series.map((s) => _.merge({}, s, {comparison: true}));
      return _.chain(this.series).zip(seriesComparisons).flatten().compact().value()[index] || this.series[0];
    } else {
      return this.series[index] || this.series[0];
    }
  }

  get metric() {
    let series = this.series || [{}];
    return (_.find(series, {primary: true}) || series[0]).metric;
  }

  chartClasses() {
    let shouldExpendLegens = this.series[0].shouldExpendLegens;
    return {
      'c-chart--explore': (this.explore && !shouldExpendLegens),
      'c-chart--comparison': (this.explore && !shouldExpendLegens) || this.comparison,
      'c-chart--comparison-explore':  this.comparison && (this.explore && !shouldExpendLegens)
    }
  }

  get comparison() {
    return !!this.query.comparison;
  }

  get breakdown() {
    return !!this.query.breakdown_dimension || !!this.multiDimentionsQueryParams;
  }

  updateCsv(res) {
    if (this.seriesTable && this.seriesTable.csv) {
      this.seriesTable.csv.basename = this.csv.basename;
      this.seriesTable.csv.loadCsvFilename(res);
      this.csv = this.seriesTable.csv;
    } else {
      if (!res.length) {
        return;
      }

      this.csv.loadResponse(res);
    }
  }

  tableData(responses) {
    let dates = _.map(responses[0].data.rows, 'x');

    return _.map(dates, (date, rowIndex) => {
      let data = {date: dates[rowIndex]};

      _.forEach(responses, (response) => {
        data[response.query.metric] = response.data.rows[rowIndex].value;
      });

      return data;
    });
  };

  updateTable(res) {
    if (!this.seriesTable)
      return;

    this.seriesTable.cols = res[0].tableCols || [
      {
        field: 'date',
        sortable: 'date',
        title: 'Date',
        format: 'date',
        show: true
      }
    ].concat(
      _.compact(this.series).map((seire) => {
        return {
          field: seire.metric,
          sortable: seire.metric,
          title: seire.metric,
          format: seire.format,
          show: true
        }
      })
    );

    this.seriesTable.loadData(res[0].tableData || this.tableData(res));
  }

  selectedSerieChanged(selectedSerie) {
    this.setSeriesOnSelectedSerie(selectedSerie);
    this.processQuery(this.query);
  }

  setSeriesOnSelectedSerie(selectedSerie) {
    if (this.multiplySelectedSerie) {
      this.series = this.multiplySelectedSerie.map((serie) => {
        return _.merge({}, selectedSerie, serie);
      });
    } else {
      this.series = [selectedSerie];
    }
  }

  selectedExploreMetricChanged(serie) {
    serie.metric = serie.selectedExploreMetric.metric;
    serie.format = serie.selectedExploreMetric.format;
    this.query.metric = _.map(this.series, (s) => `${s.seriesName}_${s.metric}_${s.type}`).join('+');
    this.processQuery(this.query);
  }
}

export let AnalyticsPanelsComponent = {
    controller: function () {},
    restrict: 'E',
    controllerAs: 'vm',
    bindings: {
      config: '=?',
      resource: '=?',
      query: '=?',
      scopeName: '@',
      analyticsExplore: '=?',
      campaignsExplore: '=?'
    }
};

export let AnalyticsPanelComponent = {
  template: (($element, $attrs) => template),
  controller: AnalyticsPanelController,
  restrict: 'E',
  require: { parent: '^analyticsPanels' },
  controllerAs: 'vm',
  bindings: {
    config: '=?',
    debug: '=?',
    decorateQuery: '=?',
    resource: '=?',
    multiplySelectedSerie: '=?',
    series: '=?',
    title: '@',
    scopeName: '@',
    seriesName: '@',
    overrideCsvFilename: '@',
    id: '@',
    seriesOptions: '=?',
    seriesOptionsSelected: '@',
    seriesLimit: '@',
    tooltipShare: '@',
    tooltipType: '@',
    contextReadySubject: '=?',
    isXAxisCategory: '=',
    isYAxisLogarithmic: '@',
    drilldownConfig: '=?',
    analyticsExplore: '@',
    campaignsExplore: '@',
    hideHeader: '=?',
    explore: '=?',
    csv: '=?',
    seriesTable: '=?',
    forceSingleYAxis: '=?',
    groupByDateChange: '=?',
    customYAxis: "=?",
    injectedNgShowValue: "=",
    hideChartHeader: "@",
    skipCsv: "@",
    areSeriesNotDateBased: "=",
    processOnlyFirstSeries: "@"
  }
};

export default angular.module('app.common.analytics.panel', [
  'ngCsv',
  ConfigsModule.name,
  ServicesModule.name,
  ResourcesModule.name
])
  .component('analyticsPanels', AnalyticsPanelsComponent)
  .component('analyticsPanel', AnalyticsPanelComponent)



/** WEBPACK FOOTER **
 ** ./src/common/analyticsPanel/analyticsPanel.js
 **/